import { keepNewToken } from './tokenRefresh';
import { clearSession, onAuthLost } from './sessionGuard';
import { portalSizeError, portalHttpError } from './portal/uploadLimit';
// API 서비스 — 백엔드 통신 레이어
const BASE = process.env.REACT_APP_API_URL || '/api';

function getToken() {
  return localStorage.getItem('token') || '';
}

async function request(method, path, body, opts) {
  var headers = { 'Content-Type': 'application/json' };
  var token = getToken();
  if (token) headers['Authorization'] = 'Bearer ' + token;

  var config = { method: method, headers: headers };
  if (body && method !== 'GET') config.body = JSON.stringify(body);

  var res = await fetch(BASE + path, config);
  if (res.status === 401) {
    if (path === '/auth/login') {
      var loginData = await res.json();
      throw new Error(loginData.error || '아이디 또는 비밀번호가 틀렸습니다.');
    }
    // 401 시 토스트만 표시 (토큰 삭제/리다이렉트 안 함 — 다른 요청에 영향 방지)
    var errData = await res.json().catch(function(){return {};});
    onAuthLost(errData.error || '');   // 토큰이 정말 끝났으면 내 계정 · 로그인으로(2026-10-28 · sessionGuard)
    throw new Error(errData.error || '인증 오류');
  }
  var data = await res.json();
  // 304 캐시 응답의 X-New-Token은 무시 (옛 토큰 덮어쓰기 방지)
  if (res.status === 200 || res.status === 201) {
    var nt = res.headers.get('X-New-Token');
    if (nt && keepNewToken(nt)) {   // 같은 계정 토큰일 때만(계정 전환 뒤 늦게 온 응답이 되돌리지 않게 · 2026-10-27)
      try { var s = getStoredAccount(); if (s) { s.token = nt; setStoredAccount(s); } } catch(e2){}
    }
  }
  if (!res.ok) throw new Error(data.error || '서버 오류');
  // 자동 언래핑: {success:true, data:...} → data 내부값 반환
  if (data && data.success === true && data.hasOwnProperty('data')) return data.data;
  return data;
}

async function uploadFiles(path, formData) {
  var token = getToken();
  var headers = {};
  if (token) headers['Authorization'] = 'Bearer ' + token;

  const res = await fetch(BASE + path, {
    method: 'POST', headers: headers, body: formData,
  });
  const data = await res.json().catch(() => null);   // nginx 413 등은 HTML — 예전엔 「Unexpected token <」가 떴다
  if (!res.ok || !data) throw new Error((data && data.error) || (path.indexOf('/portal/') === 0 ? portalHttpError(res.status) : '업로드 실패 (' + res.status + ')'));
  return data;
}

// ── Auth ──
export async function login(username, password) {
  var data = await request('POST', '/auth/login', { username: username, password: password }, { noRedirect: true });
  localStorage.setItem('token', data.token);
  localStorage.setItem('account', JSON.stringify(data.account));
  return data;
}

export function logout() {
  clearSession();   // 토큰 · 계정 · 전환 때 맡겨 둔 내 토큰까지(sessionGuard)
}

export function getStoredAccount() {
  try { return JSON.parse(localStorage.getItem('account')); } catch(e) { return null; }
}

export async function getMe() {
  return request('GET', '/auth/me');
}

// ── Projects ──
export async function getProjects(search, status, page, limit, chip, progress) {
  var q = [];
  if (search) q.push('search=' + encodeURIComponent(search));
  if (status && status !== 'all') q.push('status=' + status);
  if (page) q.push('page=' + page);
  if (limit) q.push('limit=' + limit);
  // 칩(미승인/임박/경과)·진행률 필터를 서버에서 처리 → 전체 페이지 대상 집계·조회
  if (chip) q.push('chip=' + encodeURIComponent(chip));
  if (progress) q.push('progress=' + encodeURIComponent(progress));
  var qs = q.length ? '?' + q.join('&') : '';
  var headers = { 'Content-Type': 'application/json' };
  var token = getToken();
  if (token) headers['Authorization'] = 'Bearer ' + token;
  var res = await fetch(BASE + '/projects' + qs, { headers: headers });
  // AUTH_FIX_V1: 401시 자동 로그아웃/리다이렉트 제거. 토큰 보존하고 throw만.
  if (res.status === 401) {
    var errData = await res.json().catch(function(){return {};});
    onAuthLost(errData.error || '');   // 토큰이 정말 끝났으면 내 계정 · 로그인으로(2026-10-28 · sessionGuard)
    throw new Error(errData.error || '인증 오류');
  }
  var data = await res.json();
  if (!res.ok) throw new Error(data.error || '서버 오류');
  return { projects: data.data || [], total: data.total || 0, page: data.page || 1, limit: data.limit || 50, stats: data.stats || { pending: 0, near: 0, over: 0 } };
}

export async function getProject(id) {
  return request('GET', '/projects/' + id);
}

export async function createProject(data) {
  var fd = new FormData();
  ['name','bizNo','project','ceo','ceoPhone','ceoEmail','manager','managerPhone','managerEmail','referrer','refPhone','refEmail','deadline','brand'].forEach(function(f){ fd.append(f, data[f] || ''); });
  // 자료수집 = 업무로 등록(2026-10-26) — 어느 업체 · 팀장님 · 업무인지(services/collectTask)
  if (data.taskSpec) fd.append('taskSpec', JSON.stringify(data.taskSpec));
  // 프로젝트 가이드
  if (data.projectGuides && data.projectGuides.length > 0) {
    var pgMeta = data.projectGuides.map(function(g) {
      return { type: g.type, label: g.label || '', url: g.url || '', fileName: g.fileName || '' };
    });
    fd.append('projectGuides', JSON.stringify(pgMeta));
    data.projectGuides.forEach(function(g, i) {
      if (g.type === 'file' && g.file) fd.append('projectGuideFile_' + i, g.file);
    });
  }
  if (data.items && data.items.length > 0) {
    var meta = data.items.map(function(it) {
      return { label: it.label, type: it.type, required: it.required, sensitive: !!it.sensitive, desc: it.desc || '', options: (it.options||[]).filter(function(o){return o&&o.trim();}), guides: (it.guides||[]).map(function(g){ return { type: g.type, label: g.label||'', url: g.url||'', fileName: g.fileName||'', filePath: g.filePath||'', s3Key: g.s3Key||'' }; }) };
    });
    fd.append('items', JSON.stringify(meta));
    data.items.forEach(function(it, ii) { (it.guides||[]).forEach(function(g, gi) { if (g.type==='file' && g.file) fd.append('guideFile_'+ii+'_'+gi, g.file); }); });
  }
  var token = localStorage.getItem('token');
  var res = await fetch(BASE + '/projects', { method: 'POST', headers: { 'Authorization': 'Bearer ' + token }, body: fd });
  var result = await res.json();
  if (!res.ok) throw new Error(result.error || '서버 오류');
  return result;
}

export async function updateProject(id, data) {
  return request('PUT', '/projects/' + id, data);
}

export async function closeProject(id) {
  return request('PUT', '/projects/' + id + '/close');
}

export async function reopenProject(id) {
  return request('PUT', '/projects/' + id + '/reopen');
}

/** 골라서 한꺼번에 — action: close · reopen · delete (2026-10-27) */
export async function bulkProjects(ids, action) {
  return request('POST', '/projects/bulk-action', { ids: ids, action: action });
}

export async function deleteProject(id) {
  return request('DELETE', '/projects/' + id);
}

/* 자주 쓰는 항목(2026-10-24) — 편집용 템플릿 한 벌 · 이름 치면 뜨는 목록(등록한 것 먼저 + 지금까지 쓴 항목) */
export async function getPresets() { return request('GET', '/templates/presets'); }
let libCache = null;
export function getItemLibrary() {
  if (!libCache) libCache = request('GET', '/templates/item-library').then((r) => (Array.isArray(r) ? r : (r && Array.isArray(r.data) ? r.data : [])))   // request 가 {success, data} 를 벗겨 준다.catch(() => { libCache = null; return []; });
  return libCache;
}
export function clearItemLibrary() { libCache = null; }

export async function addItem(projectId, data) {
  var formData = new FormData();
  formData.append('label', data.label || '');
  formData.append('type', data.type || 'file');
  formData.append('required', data.required ? 'true' : 'false');
  formData.append('desc', data.desc || '');
  formData.append('sensitive', data.sensitive ? 'true' : 'false');
  if (data.section) formData.append('section', data.section);   // 섹션(자주 쓰는 항목 · 2026-10-24)
  if (data.options && data.options.length > 0) {
    formData.append('options', JSON.stringify(data.options));
  }
  if (data.guides && data.guides.length > 0) {
    var guideMeta = data.guides.map(function(g) {
      // 미리 올려 둔 파일(자주 쓰는 항목 · 2026-10-24)은 S3 키를 그대로 — 새로 고른 파일만 올린다
      return { type: g.type, label: g.label || '', url: g.url || '', fileName: g.fileName || '', filePath: g.file ? '' : (g.filePath || g.s3Key || ''), s3Key: g.file ? '' : (g.s3Key || g.filePath || '') };
    });
    formData.append('guides', JSON.stringify(guideMeta));
    data.guides.forEach(function(g, i) {
      if (g.type === 'file' && g.file) {
        formData.append('guideFile_' + i, g.file);
      }
    });
  }
  var token = localStorage.getItem('token');
  var res = await fetch(BASE + '/projects/' + projectId + '/items', {
    method: 'POST',
    headers: { 'Authorization': 'Bearer ' + token },
    body: formData,
  });
  var result = await res.json();
  if (!res.ok) throw new Error(result.error || '서버 오류');
  return result;
}

export async function updateItem(projectId, itemId, data) {
  return request('PUT', '/projects/' + projectId + '/items/' + itemId, data);
}

export async function duplicateItem(projectId, itemId) {
  return request('POST', '/projects/' + projectId + '/items/' + itemId + '/duplicate');
}
export async function deleteItem(projectId, itemId) {
  return request('DELETE', '/projects/' + projectId + '/items/' + itemId);
}



export async function uploadGuideFile(projectId, itemId, file, label) {
  var fd = new FormData();
  fd.append('file', file);
  fd.append('label', label || '참조');
  var token = localStorage.getItem('token') || '';
  var res = await fetch(BASE + '/projects/' + projectId + '/items/' + itemId + '/guide-file', {
    method: 'POST', headers: { 'Authorization': 'Bearer ' + token }, body: fd
  });
  var data = await res.json();
  if (!res.ok) throw new Error(data.error || '서버 오류');
  return data;
}

export async function updateProjectGuides(projectId, guides) {
  return request('PUT', '/projects/' + projectId + '/guides', { guides: guides });
}

export async function uploadProjectGuideFile(projectId, file, label) {
  var fd = new FormData();
  fd.append('file', file);
  fd.append('label', label || '참조');
  var token = localStorage.getItem('token');
  var res = await fetch(BASE + '/projects/' + projectId + '/project-guide-file', {
    method: 'POST', headers: { 'Authorization': 'Bearer ' + token }, body: fd
  });
  var data = await res.json();
  return data;
}

export async function reorderItems(projectId, itemIds) {
  return request('PUT', '/projects/' + projectId + '/items/reorder', { itemIds: itemIds });
}

// ── Portal ──
export async function getPortalData(id) {
  return request('GET', '/portal/' + id, null, { noRedirect: true });
}

export async function getCompanyPortalData(token) {
  return request('GET', '/portal/co/' + token, null, { noRedirect: true });
}

export async function portalUpload(projectId, itemId, uploader, files) {
  const tooBig = portalSizeError(files);
  if (tooBig) throw new Error(tooBig);
  var formData = new FormData();
  formData.append('itemId', itemId);
  formData.append('uploader', uploader);
  for (var i = 0; i < files.length; i++) {
    formData.append('files', files[i]);
  }
  return uploadFiles('/portal/' + projectId + '/upload', formData);
}

export async function portalText(projectId, itemId, uploader, text) {
  return request('POST', '/portal/' + projectId + '/text', {
    itemId: itemId, uploader: uploader, text: text,
  });
}

export async function portalDelete(projectId, uploadId) {
  return request('DELETE', '/portal/' + projectId + '/upload/' + uploadId, null, { noRedirect: true });
}

export async function getGuideDownloadUrl(projectId, guideId) {
  return request('GET', '/projects/' + projectId + '/guide/' + guideId + '/download');
}
export async function getPortalProjectGuideDownloadUrl(projectId, guideId) {
  var res = await fetch(BASE + '/portal/' + projectId + '/project-guide/' + guideId + '/download');
  var data = await res.json();
  if (!data.success) throw new Error(data.error || '오류');
  return data;
}

export async function getPortalGuideDownloadUrl(projectId, guideId) {
  return request('GET', '/portal/' + projectId + '/guide/' + guideId + '/download', null, { noRedirect: true });
}

// ── Uploads (Admin) ──
export async function approveUpload(id) {
  return request('PUT', '/uploads/' + id + '/approve');
}

export async function unapproveUpload(id) {
  return request('PUT', '/uploads/' + id + '/unapprove');
}

export async function rejectUpload(id, reason) {
  return request('PUT', '/uploads/' + id + '/reject', { reason: reason });
}

export async function deleteUpload(id) {
  return request('DELETE', '/uploads/' + id);
}

export async function batchApprove(uploadIds) {
  return request('POST', '/uploads/batch-approve', { uploadIds: uploadIds });
}

export async function batchReject(uploadIds, reason) {
  return request('POST', '/uploads/batch-reject', { uploadIds: uploadIds, reason: reason });
}

export async function getDownloadUrl(id) {
  return request('GET', '/uploads/' + id + '/download');
}

// ── Templates ──
export async function getTemplates(search) {
  var q = search ? '?search=' + encodeURIComponent(search) : '';
  return request('GET', '/templates' + q);
}

export async function getTemplate(id) {
  return request('GET', '/templates/' + id);
}

export async function applyTemplateToProject(projectId, templateId) {
  return request('POST', '/projects/' + projectId + '/apply-template', { templateId: templateId });
}

export async function uploadTemplateGuideFile(file) {
  var fd = new FormData();
  fd.append('file', file);
  var token = getToken();
  var headers = {};
  if (token) headers['Authorization'] = 'Bearer ' + token;
  var res = await fetch(BASE + '/templates/upload-guide-file', { method: 'POST', headers: headers, body: fd });
  if (!res.ok) { var d = await res.json(); throw new Error(d.error || '업로드 실패'); }
  return res.json();
}

export async function createTemplate(data) {
  return request('POST', '/templates', data);
}

export async function syncTemplateFromProject(templateId, projectId) {
  return request('POST', '/templates/' + templateId + '/sync-from-project/' + projectId);
}
export async function updateTemplate(id, data) {
  return request('PUT', '/templates/' + id, data);
}

export async function deleteTemplate(id) {
  return request('DELETE', '/templates/' + id);
}

// ── Accounts ──
export async function getAccounts() {
  return request('GET', '/accounts');
}

export async function createAccount(data) {
  return request('POST', '/accounts', data);
}

export async function deleteAccount(id) { return request('DELETE', '/accounts/' + id); }

export async function deactivateAccount(id) {
  return request('PUT', '/accounts/' + id + '/deactivate');
}

export async function activateAccount(id) {
  return request('PUT', '/accounts/' + id + '/activate');
}

export async function updateAccountModules(id, modules) {
  return request('PUT', '/accounts/' + id + '/modules', { modules: modules });
}

// ── Settings ──
export async function updateBranding(data) {
  return request('PUT', '/settings/branding', data);
}

export async function uploadLogo(file) {
  var formData = new FormData();
  formData.append('logo', file);
  return uploadFiles('/settings/logo', formData);
}

export async function updateSenderNums(senderNums) {
  return request('PUT', '/settings/sender-nums', { senderNums: senderNums });
}

export async function updateSenderName(senderName) {
  return request('PUT', '/settings/sender-name', { senderName: senderName });
}

// ── Notifications ──
export async function sendNotification(data) {
  return request('POST', '/notifications/send', data);
}

export async function getNotifications(projectId) {
  return request('GET', '/notifications/' + projectId);
}

export async function generateMessage(projectId, type) {
  return request('POST', '/notifications/generate-message', { projectId: projectId, type: type });
}

export async function portalPreview(projectId, uploadId) {
  return request('GET', '/portal/' + projectId + '/preview/' + uploadId);
}

export async function batchDownload(projectId) {
  return request('POST', '/uploads/batch-download', { projectId: projectId });
}
export async function getPdfInfo(uploadId) { return request('GET', '/uploads/' + uploadId + '/pdf-info'); }
export async function pdfReorder(uploadId, pageOrder, rotations) { return request('POST', '/uploads/' + uploadId + '/pdf-reorder', { pageOrder: pageOrder, rotations: rotations || {} }); }

export async function updateReplyEmail(replyEmail) {
  return request('PUT', '/settings/reply-email', { replyEmail: replyEmail });
}
// Presets
export async function getProjectNames() { return request('GET', '/presets/project-names'); }
export async function addProjectName(name) { return request('POST', '/presets/project-names', { name: name }); }
export async function deleteProjectName(id) { return request('DELETE', '/presets/project-names/' + id); }
export async function getReferrers() { return request('GET', '/presets/referrers'); }
export async function addReferrer(data) { return request('POST', '/presets/referrers', data); }
export async function updateReferrer(id, data) { return request('PUT', '/presets/referrers/' + id, data); }
export async function deleteReferrer(id) { return request('DELETE', '/presets/referrers/' + id); }

// #20: 대량등록
export async function bulkCreate(rows) { return request('POST', '/projects/bulk', { rows: rows }); }

// #15: 회사 자동완성 (shared API 사용)
export async function searchCompanies(q) {
  try {
    var res = await fetch('/api/shared/companies?q=' + encodeURIComponent(q), {
      headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + getToken() },
    });
    var data = await res.json();
    return data.data || data || [];
  } catch(e) { return []; }
}

// ── Modules ──
export async function getModules() { return request('GET', '/modules'); }
export async function getMyModules() { return request('GET', '/modules/my'); }
export async function reorderMyModules(order) { return request('PUT', '/modules/my/reorder', { order: order }); }
export async function createModule(data) { return request('POST', '/modules', data); }
export async function updateModule(id, data) { return request('PUT', '/modules/' + id, data); }
export async function deleteModule(id) { return request('DELETE', '/modules/' + id); }
export async function getAccountModules(accountId) { return request('GET', '/modules/account/' + accountId); }
export async function setAccountModules(accountId, moduleIds) { return request('PUT', '/modules/account/' + accountId, { module_ids: moduleIds }); }

// ── Auth 추가 ──
export async function changePassword(currentPassword, newPassword) {
  return request('PUT', '/auth/password', { currentPassword, newPassword });
}
export async function resetPassword(accountId, newPassword) {
  return request('PUT', '/auth/reset-password/' + accountId, { newPassword });
}

export async function getMergedPdf(projectId, itemId) {
  return request("GET", "/projects/" + projectId + "/items/" + itemId + "/merged-download");
}
